import Konva from "konva";

import type { UiTooltipFunction } from "various-ui";
import type { KonvaRenderData } from "@/assets/js/konva";

import type { SetupContext } from "vue";
import { renderDeep } from "@/assets/js/konva";
import { disposeUpExonDistance, disposeRelatedToReal, disposeIntronOption, disposeExonOption } from "./utils";
import { VisualListSequenceModelEmits } from "../index";

import type { CanvasMrnaData, CanvasInitOption, CanvasRenderData, CanvasRangeData, CanvasExonData } from "./type";

export const useComposable = (emits: SetupContext<typeof VisualListSequenceModelEmits>["emit"]) => {
    //* 静态变量
    const variable = {
        data: shallowRef<CanvasMrnaData[]>([]), //* 转录本列表
        unionset: shallowRef<CanvasRangeData[]>([]), //* 分区数据

        size: shallowRef<number>(0), //* 分区的总长度
        ratio: shallowRef<number>(0), //* 绘制比例
        space: shallowRef<number>(0), //* 内含子的尺寸
        width: shallowRef<number>(0), //* 绘制区域宽度
    };

    //* 响应式变量
    const refs = {
        isMore: ref<boolean>(false), //* 是否显示全部
        canvas: ref<HTMLDivElement>(), //* 模块画布
        tooltip: ref<InstanceType<typeof UiTooltipFunction>>(), //* 悬浮组件
    };

    //* 绘图控制器
    const control = {
        data: shallowRef<KonvaRenderData[]>([]), //* 绘制队列
        stage: ref<Konva.Stage>(), //* 图形舞台
        layer: ref<Konva.Layer>(), //* 图形场景
        mouse: ref<Konva.Layer>(), //* Mrna范围控制器
        title: ref<Konva.Layer>(), //* 标题控制器
    };

    //* 函数列表
    const methods = {
        //* 展示全部
        more: (is?: boolean) => {
            if (!refs.canvas.value) return;
            else {
                refs.isMore.value = is != undefined ? is : !refs.isMore.value;
                if (refs.isMore.value || variable.data.value.length <= 5) {
                    refs.canvas.value.style.height = "";
                } else if (variable.width.value > refs.canvas.value.clientWidth && refs.canvas.value.clientHeight > 482) {
                    refs.canvas.value.style.height = "482px";
                    scrollTo && scrollTo({ top: refs.canvas.value.getBoundingClientRect().top + document.documentElement.scrollTop - 440 });
                } else if (refs.canvas.value.clientHeight > 470) {
                    refs.canvas.value.style.height = "470px";
                    scrollTo && scrollTo({ top: refs.canvas.value.getBoundingClientRect().top + document.documentElement.scrollTop - 440 });
                }
            }
        },

        //* 初始化函数
        init: async (data: CanvasRenderData, option?: CanvasInitOption) => {
            //* 数据源初始化
            variable.size.value = data.size || 0;
            variable.data.value = data.data || [];
            variable.space.value = option?.space || 20;
            variable.width.value = option?.width || refs.canvas.value?.clientWidth || 0;
            variable.unionset.value = data.unionset || [];

            //* 触发计算函数
            methods.calculate();

            //* 检测是否进行图像渲染
            if (option?.isNotRender) return;
            else {
                await methods.render();
                option?.callback?.();
            }
        },

        //* 数据处理函数
        calculate: () => {
            //* 计算可视区域剩余的可被外显子使用的尺寸
            const spaceSize = variable.unionset.value.length * variable.space.value;
            const res = {
                space: spaceSize,
                size: variable.width.value - spaceSize,
            };

            //* 当用于外显子绘制的可用尺寸小于内含子的两倍时, 增加滚动条并补充画布尺寸
            if (res.size < res.space * 2) {
                res.size += Math.abs(res.size) + res.space * 2;
            }

            //* 计算坐标转换比例并重新计算画布宽度
            variable.ratio.value = (res.size - 2) / variable.size.value;
            variable.width.value = res.space + res.size;
        },

        //* 渲染函数
        render: async () => {
            //* 检测是否获取到画布容器
            if (!refs.canvas.value) return;

            //* 初始化舞台
            if (control.stage.value) {
                control.stage.value?.destroyChildren(); //* 销毁所有子节点
            } else {
                control.stage.value = new Konva.Stage({ container: refs.canvas.value, width: variable.width.value });
            }

            //* 初始化场景
            control.layer.value = new Konva.Layer();
            control.mouse.value = new Konva.Layer();
            control.title.value = new Konva.Layer();

            variable.data.value.forEach((mrna, index) => {
                //* 初始化数据
                let start = 0; //* 转录本开始的位置
                let mrnaSize = 0; //* 转录本的尺寸
                const offsetX = 1; //* X轴偏移位置
                const offsetY = 38 * (index + 1) + 56 * index; //* Y轴偏移位置
                const renderData: KonvaRenderData = { el: "Group", children: [] }; //* 绘制队列数据

                //* 绘制标题
                control.title.value?.add(
                    new Konva.Text({ x: 0, y: index * 94, fill: "#6D747F", fontSize: 14, lineHeight: 2.142857142857143, text: mrna.id })
                );

                //* 生成外显子与内含子的绘制队列
                mrna.data.forEach((exon, key) => {
                    //* 初始化外显子X轴上的位置与外显子的参数
                    const x = disposeRelatedToReal(exon.start, {
                        space: variable.space.value,
                        ratio: variable.ratio.value,
                        unionset: variable.unionset.value,
                    });

                    //* 初始化外显子的参数
                    const exonOption = disposeExonOption({
                        exon,
                        offsetX,
                        offsetY,
                        space: variable.space.value,
                        ratio: variable.ratio.value,
                        unionset: variable.unionset.value,
                    });

                    //* 除第一个外显子外, 其余外显子前面都存在内含子
                    if (key != 0) {
                        //* 获取与上一个外显子之间的间隔和距离
                        const distance = disposeUpExonDistance(exon, mrna.data[key - 1], variable.unionset.value);
                        //* 初始化内含子尺寸
                        const size = distance.number * variable.space.value + distance.size * variable.ratio.value;
                        //* 生成内含子配置并添加
                        renderData.children?.push(disposeIntronOption(offsetX + x, offsetY, size));
                        //* 内含子尺寸添加
                        mrnaSize += size;
                    } else {
                        start = offsetX + x;
                    }

                    //* 生成外显子配置并添加
                    renderData.children?.push(exonOption);
                    //* 外显子尺寸添加
                    mrnaSize += exonOption.width || 0;
                });

                //* 绘制队列添加
                control.data.value.push(renderData);

                //* 创建触控区域
                const rect = new Konva.Rect({ x: start, y: offsetY, width: mrnaSize, height: 24 });

                //* 挂载事件
                rect.on("click", (ev) => emits("click", mrna, ev.evt));

                //* 绘制
                control.mouse.value?.add(rect);
            });

            //* 悬浮入场样式修改
            control.mouse.value.on("mouseenter", (ev) => {
                const node = ev.evt.target as HTMLElement;
                if (!node) return;
                else {
                    node.style.cursor = "pointer";
                }
            });

            //* 悬浮移动样式修改
            control.mouse.value.on("mousemove", (ev) => {
                const node = ev.evt.target as HTMLElement;
                if (!node) return;
                else {
                    refs.tooltip.value?.show("start", { pageX: ev.evt.pageX, pageY: ev.evt.pageY, initialization: true });
                }
            });

            //* 悬浮离开样式修改
            control.mouse.value.on("mouseleave", (ev) => {
                const node = ev.evt.target as HTMLElement;
                if (!node) return;
                else {
                    node.style.cursor = "auto";
                    refs.tooltip.value?.hidden();
                }
            });

            //* 添加场景与标题
            control.stage.value.add(control.layer.value);
            control.stage.value.add(control.mouse.value);
            control.stage.value.add(control.title.value);

            await renderDeep(0, { deep: 1, data: control.data.value }, (data) => {
                data.forEach((value, index) => {
                    Konva.Image.fromURL(value.toDataURL({ x: 0, width: variable.width.value }), (image) => {
                        //* 初始化数据
                        const offsetY = 38 * (index + 1) + 56 * index;
                        //* 绘制
                        control.layer.value?.add(image.y(offsetY));
                    });
                });

                //* 设置高度
                control.stage.value?.height(variable.data.value.length * 94);
            });

            //* 收起转录本列表
            if (variable.width.value > refs.canvas.value.clientWidth && refs.canvas.value.clientHeight > 482) {
                refs.canvas.value.style.height = "482px";
            } else if (refs.canvas.value.clientHeight > 470) {
                refs.canvas.value.style.height = "470px";
            }
        },
    };

    return { refs, control, methods, variable };
};
